//frontend/src/components/order/Orders.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import Loader from '../layouts/Loader';
import MetaData from '../layouts/MetaData';
import { 
    Package, 
    Eye, 
    Trash2, 
    AlertTriangle, 
    Calendar, 
    Clock, 
    IndianRupee,
    ShoppingBag,
    X,
    CheckCircle2,
    Loader2
} from 'lucide-react';

export default function Orders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const { user } = useSelector(state => state.authState);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const { data } = await axios.get('/api/v1/myorders');
            setOrders(data.orders);
        } catch (error) {
            toast.error(error.response?.data?.message || "Error fetching orders");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (orderId) => {
        setSelectedOrderId(orderId);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setDeleting(true);
            await axios.delete(`/api/v1/order/${selectedOrderId}`);
            setOrders(orders.filter(order => order._id !== selectedOrderId));
            toast.success('Order deleted successfully');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error deleting order');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setSelectedOrderId(null);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Order Delivered':
                return 'bg-green-100 text-green-800';
            case 'Processing':
                return 'bg-blue-100 text-blue-800';
            case 'Order Packed':
                return 'bg-yellow-100 text-yellow-800';
            case 'Order Out for Delivery':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) return <Loader />;

    return (
        <>
            <MetaData title={'My Orders'} />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
                <div className="flex items-center justify-between mb-4 sm:mb-8">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 font-['Crimson_Text'] flex items-center">
                        <Package className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-[#dc2626]" />
                        My Orders
                    </h1>
                </div>

                {orders && orders.length > 0 ? (
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        {/* Desktop View */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Order Details
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {orders.map(order => (
                                        <tr 
                                            key={order._id}
                                            className="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-900">
                                                        #{order.orderId}
                                                    </span>
                                                    <div className="flex items-center text-sm text-gray-500 mt-1">
                                                        <Calendar className="w-4 h-4 mr-1" />
                                                        {new Date(order.createdAt).toLocaleDateString()}
                                                    </div>
                                                    <div className="flex items-center text-sm text-gray-500 mt-1">
                                                        <ShoppingBag className="w-4 h-4 mr-1" />
                                                        {order.orderItems.length} item(s)
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.orderStatus)}`}>
                                                    {order.orderStatus === 'Order Delivered' ? 
                                                        <CheckCircle2 className="w-4 h-4 mr-1" /> : 
                                                        <Clock className="w-4 h-4 mr-1" />
                                                    }
                                                    {order.orderStatus}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm font-medium text-gray-900">
                                                    <IndianRupee className="w-4 h-4 mr-1" />
                                                    {order.totalPrice}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center space-x-3">
                                                    <Link 
                                                        to={`/order/${order._id}`}
                                                        className="text-[#dc2626] hover:text-[#dc2626]/80 transition-colors duration-150"
                                                        title="View Order"
                                                    >
                                                        <Eye className="w-5 h-5" />
                                                    </Link>
                                                    
                                                    {order.orderStatus !== 'Order Delivered' && (
                                                        <button
                                                            onClick={() => handleDeleteClick(order._id)}
                                                            disabled={deleting}
                                                            className="text-red-600 hover:text-red-800 transition-colors duration-150"
                                                            title="Cancel Order"
                                                        >
                                                            <Trash2 className="w-5 h-5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile View */}
                        <div className="sm:hidden">
                            <ul className="divide-y divide-gray-200">
                                {orders.map(order => (
                                    <li key={order._id} className="p-4 hover:bg-gray-50">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">#{order.orderId}</p>
                                                <div className="flex items-center text-xs text-gray-500 mt-1">
                                                    <Calendar className="w-3 h-3 mr-1" />
                                                    {new Date(order.createdAt).toLocaleDateString()}
                                                </div>
                                            </div>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.orderStatus)}`}>
                                                {order.orderStatus === 'Order Delivered' ? 
                                                    <CheckCircle2 className="w-3 h-3 mr-1" /> : 
                                                    <Clock className="w-3 h-3 mr-1" />
                                                }
                                                {order.orderStatus}
                                            </span>
                                        </div>
                                        
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="flex items-center">
                                                <div className="flex items-center text-sm text-gray-500 mr-3">
                                                    <ShoppingBag className="w-4 h-4 mr-1" />
                                                    {order.orderItems.length} item(s)
                                                </div>
                                                <div className="flex items-center text-sm font-medium text-gray-900">
                                                    <IndianRupee className="w-4 h-4 mr-1" />
                                                    {order.totalPrice}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                <Link 
                                                    to={`/order/${order._id}`}
                                                    className="text-[#dc2626] hover:text-[#dc2626]/80 transition-colors duration-150"
                                                    title="View Order"
                                                >
                                                    <Eye className="w-5 h-5" />
                                                </Link>
                                                
                                                {order.orderStatus !== 'Order Delivered' && (
                                                    <button
                                                        onClick={() => handleDeleteClick(order._id)}
                                                        disabled={deleting}
                                                        className="text-red-600 hover:text-red-800 transition-colors duration-150"
                                                        title="Cancel Order"
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 text-center">
                        <ShoppingBag className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-3 sm:mb-4" />
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Orders Found</h3>
                        <p className="text-sm sm:text-base text-gray-500">Looks like you haven't placed any orders yet.</p>
                        <Link
                            to="/shop"
                            className="mt-3 sm:mt-4 inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#dc2626] hover:bg-[#dc2626]/90 transition-colors duration-150"
                        >
                            Start Shopping
                        </Link>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div 
                            className="fixed inset-0 transition-opacity" 
                            onClick={() => setShowDeleteModal(false)}
                        >
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full w-full max-w-sm mx-4">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <AlertTriangle className="h-6 w-6 text-red-600" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Cancel Order
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to cancel this order? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                >
                                    {deleting ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Trash2 className="w-4 h-4 mr-2" />
                                    )}
                                    {deleting ? 'Cancelling...' : 'Cancel Order'}
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowDeleteModal(false)}
                                    disabled={deleting}
                                >
                                    <X className="w-4 h-4 mr-2" />
                                    Keep Order
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}